import {
  PRODUCT_GROUPS_LEGEND_ICON_LABEL_GAP_PERCENT,
  PRODUCT_GROUPS_LEGEND_ICON_OFFSET_PERCENT,
  PRODUCT_GROUPS_LEGEND_ROW_SPACING_PCT,
  SBEF_OFFSET_BOX_RATIO,
} from './report-product-groups.constants';
import { formatProductGroupsChartValue } from './report-product-groups.helpers';
import { buildProductGroupDonutSliceGeometries } from './product-groups-donut.helpers';
import type { ProductGroupSegment } from './report-product-groups.model';
import {
  PRODUCT_GROUPS_LABEL_VALUE,
  SBEF_LEGEND_TEXT_FONT_SIZE,
} from './report-product-groups.typography';

export type PlacedProductGroupLegendRow = ProductGroupSegment & {
  /** Label row anchor (% from plot top). */
  labelTopPct: number;
};

const TABULAR_CHAR_WIDTH_RATIO = 0.58;

function estimateScaledTextWidth(text: string, fontSize: number): number {
  return text.length * fontSize * TABULAR_CHAR_WIDTH_RATIO;
}

export type ProductGroupsLegendTableColumns = {
  labelWidth: string;
  co2eWidth: string;
  offsetWidth: string;
  tableWidth: string;
  hasOffsetColumn: boolean;
};

function estimateLegendTableWidthScaling(
  maxLabelW: number,
  co2eColumnW: number,
  groupGap: number,
  hasOffsetColumn: boolean,
  offsetBoxWidth: number,
): number {
  let total =
    PRODUCT_GROUPS_LEGEND_ICON_OFFSET_PERCENT +
    maxLabelW +
    groupGap +
    co2eColumnW;
  if (hasOffsetColumn) total += groupGap + offsetBoxWidth;
  return total;
}

function estimateLegendLabelColumnWidth(
  row: PlacedProductGroupLegendRow,
  fontSize: number,
  iconLabelGap: number,
  iconInnerGap: number,
): number {
  const iconCount = row.memberIds?.length ?? 1;
  const iconsWidth =
    iconCount * fontSize + Math.max(0, iconCount - 1) * iconInnerGap;
  const textWidth = estimateScaledTextWidth(row.label, fontSize);
  return iconsWidth + iconLabelGap + textWidth;
}

/**
 * Stack legend rows top-down in donut display order (largest slice first,
 * Others last) — same order as {@link buildProductGroupDonutSliceGeometries}.
 */
export const placeProductGroupsLegendRows = (
  segments: ProductGroupSegment[],
): PlacedProductGroupLegendRow[] => {
  const geometries = buildProductGroupDonutSliceGeometries(segments);
  if (geometries.length === 0) return [];

  const totalSpanPct =
    (geometries.length - 1) * PRODUCT_GROUPS_LEGEND_ROW_SPACING_PCT;
  const firstRowTopPct = (100 - totalSpanPct) / 2;

  return geometries.map((geometry, index) => {
    const {
      startAngle,
      endAngle,
      midAngle,
      proposalOuterRadiusVb,
      baselineOuterRadiusVb,
      leaderOuterRadiusVb,
      ...base
    } = geometry;
    return {
      ...base,
      labelTopPct:
        firstRowTopPct + index * PRODUCT_GROUPS_LEGEND_ROW_SPACING_PCT,
    };
  });
};

/**
 * Shared column widths for the product-groups legend table so cloud icons and
 * offset boxes align vertically across rows.
 */
export function computeProductGroupsLegendTableColumns(
  rows: PlacedProductGroupLegendRow[],
  getScalingSize: (value: number) => string,
): ProductGroupsLegendTableColumns {
  const fontSize = SBEF_LEGEND_TEXT_FONT_SIZE;
  const { innerGap, groupGap } = PRODUCT_GROUPS_LABEL_VALUE;
  const iconLabelGap = PRODUCT_GROUPS_LEGEND_ICON_LABEL_GAP_PERCENT;

  let maxLabelW = 0;
  let maxCo2eTextW = 0;
  let maxOffsetTextW = 0;
  let hasOffsetColumn = false;

  for (const row of rows) {
    maxLabelW = Math.max(
      maxLabelW,
      estimateLegendLabelColumnWidth(row, fontSize, iconLabelGap, innerGap),
    );
    maxCo2eTextW = Math.max(
      maxCo2eTextW,
      estimateScaledTextWidth(
        formatProductGroupsChartValue(row.cloudValue),
        fontSize,
      ),
    );
    if (
      row.arrowValue == null ||
      Math.round(Math.abs(row.arrowValue) * 10) === 0
    )
      continue;
    hasOffsetColumn = true;
    maxOffsetTextW = Math.max(
      maxOffsetTextW,
      estimateScaledTextWidth(
        formatProductGroupsChartValue(Math.abs(row.arrowValue)),
        fontSize,
      ),
    );
  }

  const labelWidth = getScalingSize(maxLabelW);
  const co2eColumnW = fontSize + innerGap + maxCo2eTextW;
  const co2eWidth = getScalingSize(co2eColumnW);

  if (!hasOffsetColumn) {
    return {
      labelWidth,
      co2eWidth,
      offsetWidth: '0px',
      tableWidth: getScalingSize(
        estimateLegendTableWidthScaling(
          maxLabelW,
          co2eColumnW,
          groupGap,
          false,
          0,
        ),
      ),
      hasOffsetColumn: false,
    };
  }

  const offsetBoxWidth =
    fontSize * SBEF_OFFSET_BOX_RATIO.iconHeight +
    maxOffsetTextW +
    fontSize * SBEF_OFFSET_BOX_RATIO.paddingRight +
    fontSize * SBEF_OFFSET_BOX_RATIO.borderWidth * 2;

  return {
    labelWidth,
    co2eWidth,
    offsetWidth: getScalingSize(offsetBoxWidth),
    tableWidth: getScalingSize(
      estimateLegendTableWidthScaling(
        maxLabelW,
        co2eColumnW,
        groupGap,
        true,
        offsetBoxWidth,
      ),
    ),
    hasOffsetColumn: true,
  };
}
